import { useEffect, useRef, useState } from "react";

type Row = { id: string; name: string; price: [number, number]; weeks: [number, number] };
type Props = { rows: Row[]; fmt: (k: number) => string; weeksLabel: string };

const MAX = 100; // k€ axis
const TICKS = [0, 25, 50, 75, 100];

/** Visual range chart: every project type as a bar on one shared budget axis. Hover/tap to focus. */
const RangeChart = ({ rows, fmt, weeksLabel }: Props) => {
  const ref = useRef<HTMLDivElement>(null);
  const [shown, setShown] = useState(false);
  const [active, setActive] = useState<string | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") return setShown(true);
    const io = new IntersectionObserver(([e]) => e.isIntersecting && (setShown(true), io.disconnect()), { threshold: 0.25 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <div ref={ref} className={`range-chart mt-14 rounded-2xl border border-border bg-card/40 p-5 sm:p-8 ${shown ? "is-shown" : ""}`}>
      <div className="relative">
        {/* grid */}
        <div className="pointer-events-none absolute inset-0 ml-0 sm:ml-[34%]" aria-hidden="true">
          {TICKS.map((t) => (
            <span key={t} className="absolute top-0 bottom-0 w-px bg-border/60" style={{ left: `${(t / MAX) * 100}%` }} />
          ))}
        </div>

        <ul className="relative space-y-5 sm:space-y-4">
          {rows.map((r, i) => {
            const left = (r.price[0] / MAX) * 100;
            const width = ((r.price[1] - r.price[0]) / MAX) * 100;
            const dim = active && active !== r.id;
            return (
              <li
                key={r.id}
                className={`grid gap-2 transition-opacity duration-300 sm:grid-cols-[34%_1fr] sm:items-center ${dim ? "opacity-35" : "opacity-100"}`}
                onMouseEnter={() => setActive(r.id)}
                onMouseLeave={() => setActive(null)}
                onClick={() => setActive((a) => (a === r.id ? null : r.id))}
              >
                <span className="pr-4">
                  <span className="block text-sm font-semibold text-foreground">{r.name}</span>
                  <span className={`block font-mono text-[11px] transition-colors ${active === r.id ? "text-accent" : "text-muted-foreground"}`}>
                    {fmt(r.price[0])} – {fmt(r.price[1])} · {r.weeks[0]}–{r.weeks[1]} {weeksLabel}
                  </span>
                </span>
                <div className="relative h-9">
                  <div
                    className="range-bar absolute top-1/2 h-2.5 -translate-y-1/2 rounded-full"
                    style={{ left: `${left}%`, width: `${width}%`, transitionDelay: `${i * 110}ms` }}
                  >
                    <span className="range-dot left-0" />
                    <span className="range-dot right-0" />
                  </div>
                </div>
              </li>
            );
          })}
        </ul>

        <div className="relative mt-4 h-4 sm:ml-[34%]" aria-hidden="true">
          {TICKS.map((t) => (
            <span key={t} className="absolute -translate-x-1/2 font-mono text-[10px] text-muted-foreground/70" style={{ left: `${(t / MAX) * 100}%` }}>
              {t === 0 ? "0" : `${t}k`}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

export default RangeChart;
